import { lazy, ComponentType } from "react";

type LazyImportFn = () => Promise<{ default: ComponentType<any> }>;

const SESSION_KEY = "chunk_reload_attempted";
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff: 1s, 2s, 4s

function isChunkLoadError(error: any): boolean {
  return (
    error?.name === "ChunkLoadError" ||
    error?.message?.includes("Loading chunk") ||
    error?.message?.includes("Failed to fetch dynamically imported module") ||
    error?.message?.includes("timeout")
  );
}

function retryImport(
  importFn: LazyImportFn,
  retries: number = MAX_RETRIES
): Promise<{ default: ComponentType<any> }> {
  return new Promise((resolve, reject) => {
    importFn()
      .then(resolve)
      .catch((error: any) => {
        if (retries <= 0) {
          // All retries exhausted — try a full page reload once
          if (!sessionStorage.getItem(SESSION_KEY)) {
            sessionStorage.setItem(SESSION_KEY, "true");
            window.location.reload();
            return;
          }
          // Already tried reload, give up
          sessionStorage.removeItem(SESSION_KEY);
          reject(error);
          return;
        }

        const delay = RETRY_DELAYS[MAX_RETRIES - retries] || 2000;
        console.warn(
          `Chunk load failed, retrying in ${delay}ms... (${retries} retries left)`
        );

        setTimeout(() => {
          retryImport(importFn, retries - 1).then(resolve, reject);
        }, delay);
      });
  });
}

export default function lazyWithRetry(importFn: LazyImportFn) {
  return lazy(() => retryImport(importFn));
}

// Clear the reload flag on successful page load
if (sessionStorage.getItem(SESSION_KEY)) {
  sessionStorage.removeItem(SESSION_KEY);
}
